import { useEffect, useState, useCallback } from "react";
import { api, API_BASE } from "../lib/api";
import { format, parseISO } from "date-fns";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Plus, CalendarIcon, MapPin, Clock, Edit2, Trash2, Send, Loader2, Bell, CalendarPlus } from "lucide-react";
import { toast } from "sonner";
import RichTextEditor from "../components/RichTextEditor";

const addToCalendarHref = (eventId) => {
    const token = localStorage.getItem("ccc_token") || "";
    return `${API_BASE}/events/${eventId}/ics?token=${encodeURIComponent(token)}`;
};

const empty = { title: "", description: "", event_date: "", event_time: "", location: "" };

export default function AdminEvents() {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(empty);
    const [submitting, setSubmitting] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        const r = await api.get("/events");
        setEvents(r.data.events);
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    const openCreate = () => {
        setEditing(null);
        setForm(empty);
        setOpen(true);
    };

    const openEdit = (ev) => {
        setEditing(ev);
        setForm({
            title: ev.title,
            description: ev.description || "",
            event_date: ev.event_date,
            event_time: ev.event_time || "",
            location: ev.location || "",
        });
        setOpen(true);
    };

    const save = async (e) => {
        e.preventDefault();
        if (!form.title.trim() || !form.event_date) {
            toast.error("Title and date are required");
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                title: form.title,
                description: form.description,
                event_date: form.event_date,
                event_time: form.event_time || null,
                location: form.location || null,
            };
            if (editing) {
                await api.put(`/admin/events/${editing.id}`, payload);
                toast.success("Event updated");
            } else {
                await api.post("/admin/events", payload);
                toast.success("Event created");
            }
            setOpen(false);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to save");
        } finally {
            setSubmitting(false);
        }
    };

    const doDelete = async () => {
        if (!confirmDelete) return;
        try {
            await api.delete(`/admin/events/${confirmDelete.id}`);
            toast.success("Event deleted");
            setConfirmDelete(null);
            load();
        } catch {
            toast.error("Failed to delete");
        }
    };

    const sendReminderNow = async (ev) => {
        if (!window.confirm(`Send reminder email now to all active parents for "${ev.title}"?`)) return;
        try {
            const r = await api.post(`/admin/events/${ev.id}/send-reminder-now`);
            toast.success(`Reminder queued for ${r.data.sent} parent(s)`);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to send reminder");
        }
    };

    return (
        <div className="space-y-6" data-testid="admin-events-page">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">School Calendar</div>
                    <h1 className="font-heading text-3xl font-black tracking-tight mt-1">Upcoming Events</h1>
                    <p className="text-muted-foreground text-sm mt-1">
                        Parents automatically receive a reminder email the day before each event (08:30 UTC).
                    </p>
                </div>
                <Button onClick={openCreate} className="rounded-full" data-testid="add-event-btn">
                    <Plus className="h-4 w-4 mr-2" /> New Event
                </Button>
            </div>

            {loading ? (
                <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
            ) : events.length === 0 ? (
                <div className="text-center py-16 border border-dashed border-border rounded-3xl">
                    <CalendarIcon className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground">No upcoming events yet.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {events.map((ev) => (
                        <EventCard
                            key={ev.id}
                            ev={ev}
                            onEdit={() => openEdit(ev)}
                            onDelete={() => setConfirmDelete(ev)}
                            onRemind={() => sendReminderNow(ev)}
                        />
                    ))}
                </div>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-2xl rounded-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-heading">{editing ? "Edit Event" : "New Event"}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={save} className="space-y-4">
                        <div className="space-y-2">
                            <Label>Title *</Label>
                            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required className="h-11 rounded-xl" placeholder="Annual Sports Day" data-testid="event-title-input" />
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Date *</Label>
                                <Input
                                    type="date"
                                    value={form.event_date}
                                    min={new Date().toISOString().slice(0, 10)}
                                    onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                                    required
                                    className="h-11 rounded-xl"
                                    data-testid="event-date-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Time (optional)</Label>
                                <Input
                                    type="time"
                                    value={form.event_time}
                                    onChange={(e) => setForm({ ...form, event_time: e.target.value })}
                                    className="h-11 rounded-xl"
                                    data-testid="event-time-input"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label>Location (optional)</Label>
                            <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="h-11 rounded-xl" placeholder="School Playground" data-testid="event-location-input" />
                        </div>

                        <div className="space-y-2">
                            <Label>Description</Label>
                            <RichTextEditor value={form.description} onChange={(v) => setForm({ ...form, description: v })} />
                        </div>

                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
                            <Button type="submit" disabled={submitting} className="rounded-full" data-testid="event-submit-btn">
                                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : (editing ? "Save Changes" : "Create Event")}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
                <AlertDialogContent className="rounded-2xl">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete this event?</AlertDialogTitle>
                        <AlertDialogDescription>&quot;{confirmDelete?.title}&quot; will be permanently removed.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={doDelete} className="rounded-full bg-destructive text-destructive-foreground" data-testid="confirm-delete-event">Delete</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

const EventCard = ({ ev, onEdit, onDelete, onRemind }) => {
    const d = parseISO(ev.event_date);
    return (
        <Card className="rounded-2xl" data-testid={`event-card-${ev.id}`}>
            <CardHeader>
                <div className="flex items-start gap-4">
                    <div className="shrink-0 w-16 h-16 rounded-2xl bg-primary/20 flex flex-col items-center justify-center">
                        <div className="text-xs font-bold uppercase tracking-wider text-primary-foreground">{format(d, "MMM")}</div>
                        <div className="text-2xl font-black font-heading text-primary-foreground -mt-1">{format(d, "d")}</div>
                    </div>
                    <div className="min-w-0 flex-1">
                        <CardTitle className="font-heading text-lg truncate">{ev.title}</CardTitle>
                        <CardDescription>{format(d, "EEEE, d MMMM yyyy")}</CardDescription>
                        <div className="flex flex-wrap gap-3 mt-2 text-xs text-muted-foreground">
                            {ev.event_time && (<span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {ev.event_time}</span>)}
                            {ev.location && (<span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {ev.location}</span>)}
                            {ev.reminder_sent_at && (<span className="inline-flex items-center gap-1 text-green-600"><Bell className="h-3 w-3" /> Reminder sent</span>)}
                        </div>
                    </div>
                </div>
            </CardHeader>
            {ev.description && (
                <CardContent>
                    <div className="notif-prose text-sm line-clamp-3" dangerouslySetInnerHTML={{ __html: ev.description }} />
                </CardContent>
            )}
            <CardContent className="pt-0 flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={onRemind} className="rounded-full" data-testid={`send-reminder-${ev.id}`}>
                    <Send className="h-3 w-3 mr-1" /> Send Reminder Now
                </Button>
                <Button asChild size="sm" variant="outline" className="rounded-full" data-testid={`admin-add-to-calendar-${ev.id}`}>
                    <a href={addToCalendarHref(ev.id)} download>
                        <CalendarPlus className="h-3 w-3 mr-1" /> Add to Calendar
                    </a>
                </Button>
                <Button size="sm" variant="outline" onClick={onEdit} className="rounded-full" data-testid={`edit-event-${ev.id}`}>
                    <Edit2 className="h-3 w-3 mr-1" /> Edit
                </Button>
                <Button size="sm" variant="destructive" onClick={onDelete} className="rounded-full" data-testid={`delete-event-${ev.id}`}>
                    <Trash2 className="h-3 w-3 mr-1" /> Delete
                </Button>
            </CardContent>
        </Card>
    );
};
